import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Input, Textarea.
 *
 * The shared field styling lives in one string (`fieldBase`) rather than being copy-pasted into
 * each control, because the previous version drifted: the Input hovered to `slate-300` and the
 * Select next to it did not, so two controls on the same row behaved differently under the cursor.
 * Hard-coded `slate-*` is also simply wrong in this monorepo — the admin's palette is a warm stone,
 * so a cold slate border on a parchment card reads as grey dirt. Everything below is tokens.
 *
 * `aria-invalid` drives the error styling, and it is set by <Field>, which owns the label /
 * describedby / invalid wiring. So a control turns red because it is *announced* as invalid, not
 * because someone remembered to also pass a red className — the two cannot drift apart.
 */

const fieldBase = [
  'w-full rounded-lg border border-input bg-background text-foreground',
  'transition-[color,border-color,box-shadow] duration-150',
  'placeholder:text-muted-foreground',
  'hover:border-muted-foreground/40',
  // Focus is a THIN ring, not the OS-style offset outline. `ring-offset-2` used to punch a white
  // gap between the field and a full-strength `--ring` (a near-black ink in three of the four
  // apps), which around a rounded field read as a heavy doubled black box rather than "this field
  // is active". Instead: no offset gap, the border shifts to the ring hue, and a 1px ring in that
  // same hue sits flush against it — the field reads as a single thin brand-coloured line, not a
  // bold halo. `ring-offset-0` is load-bearing: each app's global `:focus-visible` rule sets a 2px
  // ring offset, and without cancelling it here that white gap would return through the base layer.
  'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0',
  'disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60',
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:border-destructive aria-[invalid=true]:focus-visible:ring-destructive/20',
].join(' ');

/** The three heights the apps actually use. `sm` is the dense table-toolbar control. */
const sizes = {
  sm: 'h-8 px-2.5 text-sm',
  md: 'h-10 px-3 text-sm',
  lg: 'h-11 px-3.5 text-base',
} as const;

export type InputSize = keyof typeof sizes;

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  inputSize?: InputSize;
  /** Rendered inside the field, left. An icon — decorative, so it is aria-hidden and click-through. */
  leading?: React.ReactNode;
  /** Rendered inside the field, right. A unit ("kg", "$"), a spinner, a clear button. */
  trailing?: React.ReactNode;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, inputSize = 'md', leading, trailing, ...props }, ref) => {
    const input = (
      <input
        type={type}
        ref={ref}
        className={cn(
          fieldBase,
          sizes[inputSize],
          // The adornments are absolutely positioned over the field, so the text has to be padded
          // out of their way or it slides underneath them.
          leading && 'pl-9',
          trailing && 'pr-9',
          className,
        )}
        {...props}
      />
    );

    if (!leading && !trailing) return input;

    return (
      <div className="relative w-full">
        {leading && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center text-muted-foreground [&_svg]:h-4 [&_svg]:w-4"
          >
            {leading}
          </span>
        )}
        {input}
        {trailing && (
          // NOT pointer-events-none: unlike `leading`, this slot takes a clear button often enough
          // that swallowing its clicks would be the bug.
          <span className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">
            {trailing}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(fieldBase, 'min-h-[80px] resize-y px-3 py-2 text-sm', className)}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

/**
 * The base ring/border recipe, exported so the custom Select's trigger is the same box as an Input
 * rather than a lookalike. One definition, or they drift — which is exactly what happened last
 * time.
 */
export { fieldBase, sizes as fieldSizes };
